import React from 'react';

import ModifyParameterModal from '../../elements/ModifyParameterModal';
import Card from '../../elements/common/Card';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import useAutoPaginator from '../../hooks/useAutoPaginator';

import PercentagesApi, { AffiliateRevenuePercentageOwner } from 'Api/CRM/Modules/AffiliateRevenuePercentagesApi';
import GetAffiliateRevenuePercentageResponse from 'Api/CRM/Responses/AffiliateRevenuePercentageResponse';
import DeleteButton from 'crm/elements/DeleteButton';
import { PERMISSION } from 'crm/helpers/Permissions';
import { ALLOWED_ROLES } from 'crm/hooks/useAffiliateRevenueAuth';

function AffiliateRevenuePercentageList({ id, isAdmin = false }: Readonly<{ id: number; isAdmin?: boolean }>) {
    const ownerType = isAdmin ? AffiliateRevenuePercentageOwner.Admin : AffiliateRevenuePercentageOwner.Affiliate;

    const { items, updateItems, isLoading, auto } = useAutoPaginator<GetAffiliateRevenuePercentageResponse>((page) =>
        PercentagesApi.getPercentagePerLottery(id, ownerType, page)
    );

    const handleModify = async (lotteryId: number, value: string) => {
        const percentage = parseFloat(value.replace(',', '.'));
        await PercentagesApi.updateOrCreate(id, ownerType, lotteryId, percentage);
        await updateItems();
    };

    const handleDelete = async (lotteryId: number) => {
        await PercentagesApi.remove(id, ownerType, lotteryId);
        await updateItems();
    };

    return (
        <Card title="Affiliate revenue percentage per lottery (with defaults)">
            <TableLoader isLoading={isLoading}>
                <table className="table">
                    <thead>
                        <tr>
                            <th>Lottery name</th>
                            <th>Affiliate revenue</th>
                            <th aria-label="Change buttons" />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.lotteryId}>
                                <td>{item.lotteryName}</td>
                                <td className={item.isDefault ? null : 'text-dark'}>{item.percentage}%</td>
                                <td>
                                    <ModifyParameterModal
                                        parameterName="percentage"
                                        displayName="percentage"
                                        value={item.percentage}
                                        action={(value) => handleModify(item.lotteryId, value)}
                                        roles={ALLOWED_ROLES}
                                        permissions={[PERMISSION.UPDATE_AFFILIATE]}
                                    />
                                    {!item.isDefault && (
                                        <DeleteButton
                                            action={() => handleDelete(item.lotteryId)}
                                            roles={ALLOWED_ROLES}
                                            permissions={[PERMISSION.UPDATE_AFFILIATE]}
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

export default AffiliateRevenuePercentageList;
